import {
  useMemo,
  type FC,
  type PropsWithChildren,
} from 'react'
import {
  RuntimeAdapterProvider,
  fromThreadMessageLike,
  useAui,
  type ThreadHistoryAdapter,
  type ExportedMessageRepository,
  type ExportedMessageRepositoryItem,
} from '@assistant-ui/react'
import api from '@/services/api'
import type {
  InquisitorResponseData,
  ThreadHistoryTurn,
  ThreadMessagesApiResponse,
} from '@/types/chat'

function turnToRepositoryItems(
  turn: ThreadHistoryTurn,
): ExportedMessageRepositoryItem[] {
  const userId = `user-${turn.log_id}`
  const assistantId = `assistant-${turn.log_id}`
  const responseData: InquisitorResponseData = {
    log_id: turn.log_id,
    response_type: turn.response_type,
    message: turn.message,
    soft_warning: false,
    spec: turn.spec ?? null,
  }

  return [
    {
      parentId: null,
      message: fromThreadMessageLike(
        {
          id: userId,
          role: 'user',
          content: turn.question_text,
        },
        userId,
        { type: 'complete', reason: 'stop' },
      ),
    },
    {
      parentId: userId,
      message: fromThreadMessageLike(
        {
          id: assistantId,
          role: 'assistant',
          content: [
            { type: 'text', text: turn.message },
            { type: 'data', name: 'inquisitor-response', data: responseData },
          ],
          metadata: {
            custom: { ...responseData },
          },
        },
        assistantId,
        { type: 'complete', reason: 'stop' },
      ),
    },
  ]
}

async function waitForRemoteId(
  getRemoteId: () => string | undefined,
  attempts = 40,
  delayMs = 50,
): Promise<string | undefined> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const remoteId = getRemoteId()
    if (remoteId) return remoteId
    await new Promise((resolve) => setTimeout(resolve, delayMs))
  }
  return getRemoteId()
}

class ApiThreadHistoryAdapter implements ThreadHistoryAdapter {
  private getRemoteId: () => string | undefined

  constructor(getRemoteId: () => string | undefined) {
    this.getRemoteId = getRemoteId
  }

  async load(): Promise<ExportedMessageRepository> {
    const remoteId = await waitForRemoteId(this.getRemoteId)
    if (!remoteId) {
      return { messages: [] }
    }

    try {
      const { data } = await api.get<ThreadMessagesApiResponse>(
        `/threads/${remoteId}/messages`,
      )
      const messages = data.messages.flatMap(turnToRepositoryItems)
      const headId = messages.at(-1)?.message.id ?? null
      return { headId, messages }
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status
      if (status === 404) {
        return { messages: [] }
      }
      console.warn('[inquisitor] failed to load thread history', error)
      return { messages: [] }
    }
  }

  async append(_item: ExportedMessageRepositoryItem): Promise<void> {
    // Persistence is handled by POST /threads/{id}/messages on each turn.
  }
}

export const ThreadHistoryProvider: FC<PropsWithChildren> = ({ children }) => {
  const aui = useAui()
  const history = useMemo(
    () =>
      new ApiThreadHistoryAdapter(
        () => aui.threadListItem().getState().remoteId,
      ),
    [aui],
  )
  const adapters = useMemo(() => ({ history }), [history])

  return (
    <RuntimeAdapterProvider adapters={adapters}>
      {children}
    </RuntimeAdapterProvider>
  )
}
